/* eslint-disable no-restricted-globals */
import {
  BlotterRow,
  OEMSBlotterRowsUpdateMessage,
  OEMSBlotterRowUpdateMessage,
  OEMSBlotterSyncMessage,
  OEMSMessage,
} from "@enfusion-ui/core";
import {
  OEMSBlotterRowRemoveMessage,
  OEMSInitStatusMessage,
  OEMSStatusMessage,
} from "@enfusion-ui/types";
import {
  OEMS_FILTERS,
  OEMSContextState,
  OemsFilterKey,
} from "@enfusion-ui/web-core";
import { SocketController, WebWorkerMessenger } from "@enfusion-ui/web-workers";

import { ModuleInterface } from "../../types/socket";

type OEMSWorkerContextState = Omit<
  OEMSContextState,
  | "metadata"
  | "orderColumns"
  | "updateOrderColumns"
  | "initializationStep"
  | "initializationMessage"
>;

let omsBlotterRows: BlotterRow[] = [];

export const getOmsBlotterRows = () => omsBlotterRows;
export const setOmsBlotterRows = (rows: BlotterRow[]) =>
  (omsBlotterRows = rows);

let oemsContextState: OEMSWorkerContextState = {
  isInitialized: false,
  hasError: false,
  errorMessage: null,
  readyState: undefined,
};

const MessageType = {
  subscribe: "subscribe",
  unsubscribe: "unsubscribe",
  stateUpdate: "state-update",
  blotterSync: "blotter-sync",
  blotterRowsUpdate: "blotter-rows-update",
  blotterRowUpdate: "blotter-row-update",
  blotterRowRemove: "order-delete",
  initStatus: "init-status",
  initStatusCompleted: "init-status-completed",
  close: "close",
  error: "error",
  status: "status",
  routes: "routes",
  fetchCount: "fetch-count",
};

function reset(readyState?: number) {
  oemsContextState = {
    isInitialized: false,
    hasError: false,
    errorMessage: null,
    readyState,
  };
  setOmsBlotterRows([]);
}

const getSocketController = (messenger: WebWorkerMessenger, url: string) => {
  return new SocketController({
    path: `wss://${url}/oms`,
    onOpen: () => {
      messenger.broadcast({ command: "socket-open" });
    },
    onMessage: async (event, socket) => {
      const eventMessage = JSON.parse(event.data) as OEMSMessage;
      if (
        eventMessage.command === "MESSAGE" &&
        socket.readyState !== WebSocket.CLOSED &&
        socket.readyState !== WebSocket.CLOSING
      ) {
        switch (eventMessage.payload.type) {
          case MessageType.initStatus: {
            const {
              payload: { error, completed, message, applicationSessionToken },
            } = eventMessage as OEMSInitStatusMessage;
            if (error) {
              reset(socket.readyState);

              oemsContextState = {
                ...oemsContextState,
                hasError: true,
                errorMessage: message,
              };
              messenger.broadcast({
                command: MessageType.error,
                payload: {
                  hasError: true,
                  errorMessage: message,
                },
              });
              break;
            }

            if (completed) {
              socket.send(
                JSON.stringify({
                  command: "SUBSCRIBE",
                  destination: "/oms/blotter/complete",
                })
              );
              messenger.broadcast({
                command: "save-oems-session-token",
                payload: { applicationSessionToken },
              });
            }
            messenger.broadcast({
              command: MessageType.initStatus,
              payload: {
                message: message,
                completed,
              },
            });
            break;
          }
          case MessageType.status: {
            const {
              payload: { error },
            } = eventMessage as OEMSStatusMessage;

            if (error) {
              setOmsBlotterRows([]);
              messenger.broadcast({
                command: MessageType.close,
                payload: { readyState: socket.readyState },
              });
              socket.close();
            }
            break;
          }
          case MessageType.blotterSync: {
            const {
              payload: { blotterRows },
            } = eventMessage as OEMSBlotterSyncMessage;

            oemsContextState = { ...oemsContextState, isInitialized: true };
            setOmsBlotterRows(blotterRows);

            messenger.broadcast({
              command: MessageType.blotterSync,
              payload: {
                isInitialized: true,
                rows: blotterRows,
              },
            });
            break;
          }

          // Market data updated
          case MessageType.blotterRowsUpdate: {
            const {
              payload: { blotterRows },
            } = eventMessage as OEMSBlotterRowsUpdateMessage;

            let existingBlotterRows = getOmsBlotterRows();

            const transactionalRows: {
              add: BlotterRow[];
              update: BlotterRow[];
              idx: Record<number, number>;
            } = {
              add: [],
              update: [],
              idx: {},
            };
            blotterRows.forEach((updatedRow: BlotterRow) => {
              const { orderId: updatedOrderId } = updatedRow;
              const idx = existingBlotterRows.findIndex(
                (row: BlotterRow) => updatedOrderId === row.orderId
              );

              if (idx > -1) {
                transactionalRows.update.push(updatedRow);
                transactionalRows.idx[updatedOrderId] = idx;
              } else transactionalRows.add.push(updatedRow);
            });

            transactionalRows.update.forEach((e: BlotterRow) => {
              existingBlotterRows[transactionalRows.idx[e.orderId]] = e;
            });

            transactionalRows.add.forEach((e: BlotterRow) => {
              existingBlotterRows = [e, ...existingBlotterRows];
            });

            if (transactionalRows.add && transactionalRows.add.length > 0) {
              setOmsBlotterRows(existingBlotterRows);
            }

            messenger.broadcast({
              command: MessageType.blotterRowsUpdate,
              payload: {
                add: transactionalRows.add,
                update: transactionalRows.update,
              },
            });
            break;
          }
          case MessageType.blotterRowUpdate: {
            const {
              payload: { newOrder, blotterRow },
            } = eventMessage as OEMSBlotterRowUpdateMessage;

            const existingBlotterRows = getOmsBlotterRows();

            let index = -1;
            if (newOrder) {
              setOmsBlotterRows([blotterRow, ...existingBlotterRows]);
            } else {
              index = existingBlotterRows.findIndex(
                (row: BlotterRow) => row.orderId === blotterRow.orderId
              );
              if (index !== -1) {
                existingBlotterRows[index] = blotterRow;
              }
            }

            messenger.broadcast({
              command: MessageType.blotterRowUpdate,
              payload: {
                newOrder,
                row: blotterRow,
              },
            });

            break;
          }
          case MessageType.blotterRowRemove: {
            const {
              payload: { orderId },
            } = eventMessage as OEMSBlotterRowRemoveMessage;

            const existingBlotterRows = getOmsBlotterRows();

            const index = existingBlotterRows.findIndex(
              (row: BlotterRow) => row.orderId === orderId
            );
            const rowToDelete = existingBlotterRows[index];

            if (index !== -1) {
              existingBlotterRows.splice(index, 1);
            }

            messenger.broadcast({
              command: MessageType.blotterRowRemove,
              payload: { ...eventMessage.payload, rowToDelete },
            });

            break;
          }

          default: {
            console.error(
              `Unexpected message type: ${JSON.stringify(eventMessage)}`
            );
          }
        }
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any, socket) => {
      console.error(`Error in OMS Worker: ${JSON.stringify(err)}`);
      reset(socket.readyState);
      oemsContextState = {
        ...oemsContextState,
        hasError: true,
        errorMessage: err?.message || JSON.stringify(err),
      };
      messenger.broadcast({
        command: MessageType.error,
        payload: {
          hasError: true,
          errorMessage: err?.message || JSON.stringify(err),
        },
      });
    },
    onClose: (ev, socket) => {
      console.warn("OMS socket close", ev);
      reset(socket.readyState);
      messenger.broadcast({
        command: MessageType.close,
        payload: { readyState: socket.readyState },
      });
    },
    onDisconnect: (socket) => {
      reset(socket.readyState);
      messenger.broadcast({
        command: MessageType.close,
        payload: { readyState: socket.readyState },
      });
    },
  });
};

export const OMSModule = () => {
  const messenger = new WebWorkerMessenger();
  let socketController: SocketController<any>;

  const enable = (
    postMessage: (message: Record<string, unknown>) => void,
    url: string
  ) => {
    socketController = getSocketController(messenger, url);
    messenger.send = postMessage;
    socketController.init();
  };

  const disable = () => {
    socketController.close("oms");
    reset();
  };

  const getCurrentState = () => {
    return {
      socketStatus: socketController.socketStatus,
      currentState: oemsContextState,
      rows: getOmsBlotterRows(),
    };
  };

  const onTerminate = () => {
    socketController.close("oms");
  };

  const onMessage = (_message: Record<string, any>) => {
    const { payload, command } = _message;
    switch (command) {
      case "socket-open": {
        const { userName, passCode } = payload;
        const connectMessage = passCode.length
          ? {
              command: "CONNECT",
              login: userName,
              passcode: passCode,
              "heart-beat": 8000,
              "accept-version": "1.2",
            }
          : {
              command: "CONNECT",
              "heart-beat": 8000,
              "accept-version": "1.2",
            };
        let message = JSON.stringify(connectMessage);
        socketController.send(message);
        messenger.send({
          command: "log-message",
          payload: message,
        });

        const subscribeMessage = {
          command: "SUBSCRIBE",
          destination: "/oms",
        };
        message = JSON.stringify(subscribeMessage);
        socketController.send(message);
        messenger.send({
          command: "log-message",
          payload: message,
        });
        if (passCode.length)
          setInterval(() => {
            socketController.send("\n");
            messenger.send({ command: "log-message", payload: "heartbeat" });
          }, 30000);
        break;
      }

      case MessageType.routes: {
        const { orderId } = payload;
        const omsBlotterRows = getOmsBlotterRows();

        return {
          rows: omsBlotterRows.filter(({ columnValues }) => {
            return (
              typeof orderId === "number" &&
              (columnValues.ParentOrderId === orderId ||
                (columnValues.Method !== "Undecided" &&
                  columnValues.OrderId === orderId))
            );
          }),
        };
      }
      case MessageType.fetchCount: {
        const { filterKey } = payload;
        const omsBlotterRows = getOmsBlotterRows();

        return {
          count: (
            OEMS_FILTERS[filterKey as OemsFilterKey].filter(omsBlotterRows) ??
            []
          ).length,
        };
      }
    }
  };

  return {
    enable,
    disable,
    onTerminate,
    getCurrentState,
    onMessage,
  } as ModuleInterface;
};
